import React from 'react';
import { Box, Typography, Skeleton, Alert, Grid, Link } from '@mui/material';
import { ANNUAL_REPORT_TECHNICAL_ASSISTANCE_FORM_URL } from '../../constants/annualReports';
import { useApiGet } from '../../utils/httpClientTs';
import { getDashboard } from '../../endpoints/annualReportsTs';
import { AnnualReportsDashboardResponse } from '../../types/annualReports';
import NotificationsSection from './components/NotificationsSection';
import FormCard from './components/FormCard';
import GraphTile from './components/GraphTile';

const AnnualReportsDashboard: React.FC = () => {
  const { data, isLoading, error } = useApiGet<AnnualReportsDashboardResponse>(
    ['annual-reports-dashboard'],
    getDashboard,
    {
      enabled: true,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          Failed to load dashboard. Please try refreshing the page.
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box>
        <Alert severity="info">No dashboard data available.</Alert>
      </Box>
    );
  }

  const displayTitle = data.display_context_name
    ? `Annual Reports for ${data.display_context_name}`
    : data.display_scope === 'international'
      ? 'Annual Reports Overview'
      : 'Annual Reports Dashboard';
  const displaySubtitle =
    data.display_scope === 'international' ? 'All annual reporting forms' : '';

  const comp = data.components;
  if (!comp) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Annual Reports Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Year: {data.year}
        </Typography>
        <NotificationsSection notifications={data.notifications} />
      </Box>
    );
  }

  // Collect all graph tiles so they render in a single grid (2 per row on large screens)
  const graphTiles: Array<{
    key: string;
    title: string;
    summary: NonNullable<typeof comp.roster_dues_lc>['graph'];
    formType:
      | 'roster_dues_lc'
      | 'roster_dues_spd'
      | 'irs_lc'
      | 'irs_sc'
      | 'irs_rg';
  }> = [];
  if (comp.roster_dues_lc?.show_graph && comp.roster_dues_lc.graph) {
    graphTiles.push({
      key: 'roster_dues_lc',
      title: 'Local Chapter Roster & Dues',
      summary: comp.roster_dues_lc.graph,
      formType: 'roster_dues_lc',
    });
  }
  if (comp.roster_dues_spd?.show_graph && comp.roster_dues_spd.graph) {
    graphTiles.push({
      key: 'roster_dues_spd',
      title: 'S/P/D Chapter Dues',
      summary: comp.roster_dues_spd.graph,
      formType: 'roster_dues_spd',
    });
  }
  if (comp.irs_lc.show_graph && comp.irs_lc.graph) {
    graphTiles.push({
      key: 'irs_lc',
      title: 'IRS-LC',
      summary: comp.irs_lc.graph,
      formType: 'irs_lc',
    });
  }
  if (comp.irs_sc.show_graph && comp.irs_sc.graph) {
    graphTiles.push({
      key: 'irs_sc',
      title: 'IRS-SC/PC',
      summary: comp.irs_sc.graph,
      formType: 'irs_sc',
    });
  }
  if (comp.irs_rg.show_graph && comp.irs_rg.graph) {
    graphTiles.push({
      key: 'irs_rg',
      title: 'IRS-RG',
      summary: comp.irs_rg.graph,
      formType: 'irs_rg',
    });
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {displayTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {displaySubtitle || `Year: ${data.year}`}
      </Typography>

      <NotificationsSection notifications={data.notifications} />

      <Typography variant="body2" sx={{ mb: 2 }}>
        <Link
          href={ANNUAL_REPORT_TECHNICAL_ASSISTANCE_FORM_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Need Help?
        </Link>
      </Typography>

      <Grid container spacing={2}>
        {/* Roster & Dues LC: form cards only */}
        {comp.roster_dues_lc?.show_form &&
          comp.roster_dues_lc.chapter_forms &&
          comp.roster_dues_lc.chapter_forms.length > 0 && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {comp.roster_dues_lc.chapter_forms.map((cf) => (
                  <FormCard
                    key={cf.chapter_id}
                    title="Local Chapter Roster & Dues"
                    form={cf.form}
                    summaryLink={
                      cf.form
                        ? `/annual-reports/roster-dues/${cf.form.form_id}/members`
                        : '#'
                    }
                    contextName={cf.chapter_name}
                  />
                ))}
              </Grid>
            </Grid>
          )}

        {/* Roster & Dues SPD: form cards only */}
        {comp.roster_dues_spd?.show_form &&
          comp.roster_dues_spd.chapter_forms &&
          comp.roster_dues_spd.chapter_forms.length > 0 && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {comp.roster_dues_spd.chapter_forms.map((cf) => (
                  <FormCard
                    key={cf.chapter_id}
                    title="S/P/D Chapter Dues"
                    form={cf.form}
                    summaryLink={
                      cf.form
                        ? `/annual-reports/roster-dues/${cf.form.form_id}/chapters`
                        : '#'
                    }
                    contextName={cf.chapter_name}
                  />
                ))}
              </Grid>
            </Grid>
          )}

        {/* IRS-LC: form cards only */}
        {comp.irs_lc.show_form &&
          comp.irs_lc.chapter_forms &&
          comp.irs_lc.chapter_forms.length > 0 && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {comp.irs_lc.chapter_forms.map((cf) => (
                  <FormCard
                    key={cf.chapter_id}
                    title="IRS-LC"
                    form={cf.form}
                    summaryLink={
                      cf.form
                        ? `/annual-reports/irs-forms/lc/${cf.form.form_id}/summary`
                        : '#'
                    }
                    contextName={cf.chapter_name}
                  />
                ))}
              </Grid>
            </Grid>
          )}

        {/* IRS-SC: single form only */}
        {comp.irs_sc.show_form && comp.irs_sc.form && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <FormCard
                title="IRS-SC/PC"
                form={comp.irs_sc.form}
                summaryLink={`/annual-reports/irs-forms/sc/${comp.irs_sc.form.form_id}/summary`}
                contextName="SPD Chapter"
              />
            </Grid>
          </Grid>
        )}

        {/* All graph tiles in a single grid (2 per row on md+) */}
        {graphTiles.length > 0 && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {graphTiles.map((tile) => (
                <GraphTile
                  key={tile.key}
                  title={tile.title}
                  summary={tile.summary}
                  formType={tile.formType}
                />
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AnnualReportsDashboard;
